import React, { useMemo } from "react";
import { Reorder } from "framer-motion";

import CustomUTMInspectionOverviewForm from "../../../forms/custom-utm/customUTMInspectionOverviewForm";
import CustomNewItem from "../../custom-general/custom-new-item";
import CustomUTMInspectionFormNewItem from "../../../forms/custom-utm/customUTMInspectionFormNewItem";
import CustomUTMInspectionItem from "../custom-utm-inspection-item";
import MUTIsoPageForm from "../../../forms/mut/mutIsoPageForm";
import ReorderItem from "../../../common/reorderItem";
import Zippy from "../../../themed/zippy";

import { CustomUTMOverview, PDFImageProps, SelectOption, UTMInspection } from "../../../../utils/models";
import { matchPhrases } from "../../../../utils/utilities";
import { getUTMPipingInspectionOptions } from "../../../../utils/data";

import toastService from "../../../../services/toastService";

type UTMOverview = Omit<CustomUTMOverview, 'data'>;

type Props = {
    options?: SelectOption[];
	tmlSectionLabel?: string;
    utmOverview: UTMOverview;
	tmlLocations: PDFImageProps[];
    utmItems: UTMInspection[];
	onChangeTMLLocations: (locations: PDFImageProps[]) => void;
    onOverviewChange: (overview: UTMOverview) => void;
    onItemsChange: (items: UTMInspection[]) => void;
}

const CustomUTMSection: React.FC<Props> = ({ options = getUTMPipingInspectionOptions(), utmOverview, tmlLocations, onChangeTMLLocations, onOverviewChange, utmItems, onItemsChange, tmlSectionLabel = "TML Location" }) => {
    const handleAddInspectionItem = (newItem: UTMInspection) => {
		const inspectionItemMatch = utmItems.find((item) => matchPhrases(item.sectionTitle, newItem.sectionTitle));
		if (inspectionItemMatch) return toastService.error(`Section ${inspectionItemMatch.sectionTitle} already exists!`);

		onItemsChange([...utmItems, newItem]);
	};
	
	const handleEditInspectionItem = (originalItem: UTMInspection, updatedItem: UTMInspection) => {
		const updatedItems = utmItems.map((item) => item.sectionTitle === originalItem.sectionTitle ? updatedItem : item);
		onItemsChange(updatedItems);
	};

	const handleDeleteInspectionItem = (itemToDelete: UTMInspection) => {
		const items = utmItems.filter((item) => item.sectionTitle !== itemToDelete.sectionTitle);
		onItemsChange(items);
	};

    const inspectionItemReadingsMissing = useMemo(() => {
		if (utmItems.length === 0) return true;

		return utmItems.some((item) => !item.sectionTitle || item.items.length === 0);
	}, [utmItems]);

    return ( 
        <>
			<Zippy headerTitle={tmlSectionLabel} isValid={tmlLocations.length > 0}>
				<MUTIsoPageForm initialImages={tmlLocations} onProceed={onChangeTMLLocations} />
			</Zippy>

			<Zippy headerTitle="UTM Inspection Overview" isValid={utmOverview.velocity ? utmOverview.velocity > 0 : false}>
				<CustomUTMInspectionOverviewForm initialData={utmOverview} onProceed={onOverviewChange} />
			</Zippy>

			<Zippy headerTitle="UTM Inspection Items" isValid={!inspectionItemReadingsMissing}>
				<CustomNewItem label='Add New UTM Item'>
					<CustomUTMInspectionFormNewItem onAdd={handleAddInspectionItem} />
				</CustomNewItem>

				<Reorder.Group className="list-unstyled" values={utmItems} onReorder={onItemsChange}>
					{utmItems.map((item, index) => (
						<ReorderItem key={item.sectionTitle} value={item}>
							<CustomUTMInspectionItem
								isOpenOnDefault={index === (utmItems.length - 1)}
								onDeleteInspectionItem={handleDeleteInspectionItem}
								includeSize={false}
								options={options}
								corrosionAllowance={utmOverview.corrosionAllowance} 
								initialData={item} 
								onEditInspectionItem={(updatedItem) => handleEditInspectionItem(item, updatedItem)}
							/>
						</ReorderItem>
					))}
				</Reorder.Group>
			</Zippy>
        </>
    );
}
 
export default CustomUTMSection;